import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useGsapReveal } from "@/shared/hooks/use-gsap-reveal";
import { cn } from "@/shared/lib/utils";
import { Baby, CheckCircle2, Facebook, Globe, Instagram, Linkedin, Send } from "lucide-react";
import { useState } from "react";
import { usePublicActions } from "./public-actions";
import { TEAM, TEAM_GROUPS, type TeamMember } from "./team-data";

/** TikTok brand glyph — lucide žádnou TikTok ikonu nemá, proto inline SVG. */
function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

/** Iniciály pro fallback-avatar (přeskočí tituly jako „Ing.“, „MBA“). */
function getInitials(name: string): string {
  const words = name
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}]/gu, ""))
    .filter((w) => w.length > 1 && !["Ing", "MBA", "Mgr", "Bc", "PhD"].includes(w));
  return words
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");
}

export function TeamPage() {
  const [active, setActive] = useState<TeamMember | null>(null);
  const { openKontakt } = usePublicActions();
  const gridRef = useGsapReveal<HTMLDivElement>({
    childrenSelector: "[data-team-card]",
    stagger: 0.1,
  });

  const handleContact = (member: TeamMember) => {
    setActive(null);
    openKontakt({
      initialMessage: `Mám zájem o konzultaci s: ${member.name}.\n\n`,
    });
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-16" data-testid="hca-team-page">
      <header className="mb-10 max-w-3xl">
        <div className="flex items-center gap-3">
          <span aria-hidden="true" className="h-px w-8 bg-[color:var(--hca-gold)]" />
          <div className="font-semibold text-[color:var(--hca-gold)] text-sm uppercase tracking-[0.22em]">
            Lidé za HCA Moravia
          </div>
        </div>
        <h1 className="mt-3 text-4xl font-bold tracking-tight">Náš tým</h1>
        <p className="mt-3 text-muted-foreground">
          Konzultanti a lektoři s reálnou zkušeností z vedení firem. Klikněte na kartu pro detail a
          možnost se s&nbsp;lektorem rovnou spojit.
        </p>
      </header>

      <div ref={gridRef} className="space-y-12">
        {TEAM_GROUPS.map((g) => {
          const members = TEAM.filter((m) => m.group === g.key);
          if (members.length === 0) return null;
          return (
            <section key={g.key} data-testid={`hca-team-group-${g.key}`}>
              <div className="mb-5 flex items-center gap-3">
                <span aria-hidden="true" className="h-px w-8 bg-[color:var(--hca-gold)]" />
                <h2 className="font-bold text-2xl tracking-tight">{g.title}</h2>
              </div>
              <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3">
                {members.map((m) => (
                  <button
                    key={m.slug}
                    data-team-card
                    type="button"
                    onClick={() => setActive(m)}
                    className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card text-left transition hover:border-[color:var(--hca-gold)]/60 hover:shadow-md"
                    data-testid={`hca-team-card-${m.slug}`}
                  >
                    <div className="relative aspect-[3/4] overflow-hidden bg-muted">
                      {m.photoSrc ? (
                        <img
                          src={m.photoSrc}
                          alt={m.photoAlt ?? m.name}
                          className={cn(
                            "absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]",
                            m.onLeave && "grayscale",
                          )}
                          loading="lazy"
                        />
                      ) : (
                        <div
                          aria-hidden="true"
                          className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[color:var(--hca-navy)] to-black/70"
                        >
                          <span className="font-bold text-5xl text-[color:var(--hca-gold)] tracking-wide">
                            {getInitials(m.name)}
                          </span>
                        </div>
                      )}
                      {m.onLeave ? (
                        <span
                          className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-[color:var(--hca-navy)]/85 px-2.5 py-1 font-medium text-white text-xs backdrop-blur"
                          data-testid={`hca-team-onleave-badge-${m.slug}`}
                        >
                          <Baby className="h-3.5 w-3.5" aria-hidden="true" />
                          {m.onLeave}
                        </span>
                      ) : null}
                    </div>
                    <div className="space-y-3 p-5">
                      <div>
                        <div className="font-semibold text-lg">{m.name}</div>
                        <div className="text-muted-foreground text-sm">{m.role}</div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {m.specialties.map((s) => (
                          <span
                            key={s}
                            className="inline-flex items-center rounded-full border border-[color:var(--hca-gold)]/30 bg-[color:var(--hca-gold)]/10 px-2.5 py-0.5 font-medium text-[color:var(--hca-gold)] text-xs"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <Dialog
        open={active !== null}
        onOpenChange={(o) => {
          if (!o) setActive(null);
        }}
      >
        <DialogContent
          className="max-h-[92vh] max-w-2xl overflow-y-auto"
          data-testid="hca-team-member-dialog"
        >
          {active ? (
            <>
              <DialogTitle className="text-2xl">{active.name}</DialogTitle>
              <DialogDescription className="text-base text-muted-foreground">
                {active.role}
              </DialogDescription>
              {active.onLeave ? (
                <div
                  className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[color:var(--hca-gold)]/15 px-3 py-1 font-medium text-[color:var(--hca-gold)] text-xs"
                  data-testid="hca-team-member-onleave"
                >
                  <Baby className="h-3.5 w-3.5" aria-hidden="true" />
                  Aktuálně na mateřské dovolené
                </div>
              ) : null}
              <div className="mt-4 grid gap-5 sm:grid-cols-[200px_1fr]">
                {active.photoSrc ? (
                  <img
                    src={active.photoSrc}
                    alt={active.photoAlt ?? active.name}
                    className={cn(
                      "aspect-[3/4] w-full rounded-md object-cover",
                      active.onLeave && "grayscale",
                    )}
                  />
                ) : (
                  <div
                    aria-hidden="true"
                    className="flex aspect-[3/4] w-full items-center justify-center rounded-md bg-gradient-to-br from-[color:var(--hca-navy)] to-black/70"
                  >
                    <span className="font-bold text-6xl text-[color:var(--hca-gold)] tracking-wide">
                      {getInitials(active.name)}
                    </span>
                  </div>
                )}
                <div className="space-y-4 text-sm">
                  <p className="font-medium text-base text-foreground leading-relaxed">
                    {active.hook}
                  </p>
                  <ul className="space-y-2">
                    {active.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2 text-foreground">
                        <CheckCircle2
                          className="mt-0.5 h-4 w-4 flex-shrink-0 text-[color:var(--hca-gold)]"
                          aria-hidden="true"
                        />
                        <span className="leading-relaxed">{h}</span>
                      </li>
                    ))}
                  </ul>
                  {active.bio ? (
                    <p className="text-muted-foreground text-sm leading-relaxed">{active.bio}</p>
                  ) : null}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {active.specialties.map((s) => (
                      <span
                        key={s}
                        className="inline-flex items-center rounded-full border border-[color:var(--hca-gold)]/30 bg-[color:var(--hca-gold)]/10 px-2.5 py-0.5 font-medium text-[color:var(--hca-gold)] text-xs"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                  {active.quote ? (
                    <blockquote className="border-[color:var(--hca-gold)] border-l-2 pl-4 text-muted-foreground text-sm italic leading-relaxed">
                      „{active.quote}“
                    </blockquote>
                  ) : null}
                  {active.linkedinUrl ||
                  active.instagramUrl ||
                  active.facebookUrl ||
                  active.tiktokUrl ||
                  active.webUrl ? (
                    <div className="flex items-center gap-2 pt-1">
                      {active.linkedinUrl ? (
                        <a
                          href={active.linkedinUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                          aria-label={`LinkedIn — ${active.name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
                          data-testid={`hca-team-linkedin-${active.slug}`}
                        >
                          <Linkedin className="h-4 w-4" aria-hidden="true" />
                        </a>
                      ) : null}
                      {active.instagramUrl ? (
                        <a
                          href={active.instagramUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                          aria-label={`Instagram — ${active.name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
                          data-testid={`hca-team-instagram-${active.slug}`}
                        >
                          <Instagram className="h-4 w-4" aria-hidden="true" />
                        </a>
                      ) : null}
                      {active.facebookUrl ? (
                        <a
                          href={active.facebookUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                          aria-label={`Facebook — ${active.name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
                          data-testid={`hca-team-facebook-${active.slug}`}
                        >
                          <Facebook className="h-4 w-4" aria-hidden="true" />
                        </a>
                      ) : null}
                      {active.tiktokUrl ? (
                        <a
                          href={active.tiktokUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                          aria-label={`TikTok — ${active.name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
                          data-testid={`hca-team-tiktok-${active.slug}`}
                        >
                          <TikTokIcon className="h-4 w-4" />
                        </a>
                      ) : null}
                      {active.webUrl ? (
                        <a
                          href={active.webUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                          aria-label={`Web — ${active.name}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
                          data-testid={`hca-team-web-${active.slug}`}
                        >
                          <Globe className="h-4 w-4" aria-hidden="true" />
                        </a>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="mt-6 flex flex-col gap-2 border-t pt-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-muted-foreground text-sm">
                  {active.onLeave
                    ? "Tento člen týmu je nyní na rodičovské dovolené — rádi vás spojíme s někým z týmu."
                    : "Máte zájem o konzultaci nebo spolupráci? Napište nám."}
                </p>
                <Button
                  type="button"
                  onClick={() => handleContact(active)}
                  data-testid={`hca-team-contact-${active.slug}`}
                >
                  <Send className="h-4 w-4" aria-hidden="true" />
                  {active.onLeave ? "Napsat týmu" : "Sjednat schůzku"}
                </Button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
